import { NotificationType, setIsOverlayLogin, useAppDispatch, useAppSelector, useAppSettingsGate } from "@next-platform/client-common-core";
import { useApplicationCapabilityReady } from "@next-platform/client-common-core/infra/application/capabilities";
import { ProgressBar } from "@next-platform/client-common-spicy-ui";
import useLocalizationGate from "@src/hooks/useLocalizationGate";
import { useCallback, useEffect } from "react";
import { Outlet, useSearchParams } from "react-router-dom";

import { Spinner } from "@/components/customComponents";
import MediaFileDetailsModal from "@/components/MediaFileDetailsModal/MediaFileDetailsModal";
import MinimalHeader from "@/components/navbar/MinimalHeader";
import NavigationHeader from "@/components/navbar/NavigationHeader";
import { PartnerNavigationBar } from "@/components/navbar/PartnerNavigationBar.jsx";
import { Navigation } from "@/components/Navigation";
import { OverlayLogin } from "@/components/overlays";
import { LOCKED_FLOW_QUERY_KEY, LOCKED_FLOW_QUERY_VALUE, RELATIVE_ROUTES } from "@/constants";
import { useMediaStore, useToast } from "@/hooks";
import useAutoLangugeSelect from "@/hooks/useAutoLangugeSelect";
import useIsMultiLangEnabled from "@/hooks/useIsMultiLangEnabled";
import useLocalizedLocation from "@/hooks/useLocalizedLocation";
import { MediaStore } from "@/providers/MediaProvider";
import { getIsPartner } from "@/utils/getIsPartner";

import { MinimalChromeContext } from "./MinimalChromeContext";

const PersistentTopBar = () => {
  const [urlSearchParams] = useSearchParams();
  const isLockedFlow = urlSearchParams.get(LOCKED_FLOW_QUERY_KEY) === LOCKED_FLOW_QUERY_VALUE;
  const isMobileApp = useMediaStore((store: MediaStore) => store.isMobileApp);
  const isPartner = getIsPartner();
  const { pagePath } = useLocalizedLocation();

  const isMinimalHeaderActive = pagePath.slice(1) === RELATIVE_ROUTES.getAccess || isLockedFlow;
  const isClientNavVisibile = !isPartner && !isMobileApp && !isMinimalHeaderActive;

  const dispatch = useAppDispatch();
  const isOverlayLogin = useAppSelector((state) => state?.navigation?.isOverlayLogin);
  const handleOverlayLoginClose = useCallback(() => dispatch(setIsOverlayLogin(false)), [dispatch]);

  const { isMultiLangEnabled } = useIsMultiLangEnabled();
  const { isChangingLanguage } = useAutoLangugeSelect();
  const { isLocalizationReady } = useLocalizationGate();
  const isCapabilitiesReady = useApplicationCapabilityReady();
  const { isAppSettingsReady, errorMessage: appSettingsErrorMessage } = useAppSettingsGate();
  const { displayToast } = useToast();

  useEffect(() => {
    if (!appSettingsErrorMessage) {
      return;
    }

    displayToast({ message: appSettingsErrorMessage, type: NotificationType.error });
  }, [appSettingsErrorMessage, displayToast]);

  let topBarElement = null;
  if (isPartner) {
    topBarElement = <PartnerNavigationBar />;
  } else if (isMinimalHeaderActive) {
    topBarElement = <MinimalHeader />;
  } else if (isClientNavVisibile) {
    topBarElement = <NavigationHeader />;
  }

  if (!isAppSettingsReady || !isLocalizationReady || !isCapabilitiesReady) {
    return <Spinner centerToScreen={true} />;
  }

  return (
    <>
      {isMultiLangEnabled ? <ProgressBar isActive={isChangingLanguage} simulatedCap={75} /> : null}
      <div className={"grid h-full " + (isClientNavVisibile ? "grid-cols-[min-content,minmax(0px,1fr)]" : "!grid-cols-1")}>
        {isClientNavVisibile ? <Navigation /> : null}
        {!isClientNavVisibile && isOverlayLogin ? <OverlayLogin onClose={handleOverlayLoginClose} /> : null}
        <div className="grow flex flex-col relative">
          {topBarElement}
          <MinimalChromeContext.Provider value={isMinimalHeaderActive}>
            <Outlet />
          </MinimalChromeContext.Provider>
          {/* Mounted once, above every route group, so any surface can open a file's details over whatever it is
              showing. Each group mounts its own AppLayout, so a modal mounted there came back as a new instance
              on a link into another group, and that instance reopened the file the modal had just left. */}
          <MediaFileDetailsModal />
        </div>
      </div>
    </>
  );
};

export default PersistentTopBar;
